import {getRelativePosition} from 'chart.js/helpers'
import theme from '../../../themes/pTheme/pTheme'
import {LINE_CHAR_LIMIT} from '../../Config/config'
import {changeLabelColorOnEditToggle} from '../../common/markerHelpers'

const findMaxNumberOfLine = (labels) => {
  let maxNumberOfLine = 0

  for (const key in labels) {
    const str = labels[key]
    const numLines =
      str.length % LINE_CHAR_LIMIT === 0 ? str.length / LINE_CHAR_LIMIT : Math.floor(str.length / LINE_CHAR_LIMIT) + 1
    if (numLines > maxNumberOfLine) maxNumberOfLine = numLines
  }
  return maxNumberOfLine
}
export const updateLabelsAlignments = (labels, currentLabelLength, chart, id = null, update = false) => {
  const maxNumberOfLine = findMaxNumberOfLine(labels)
  if (currentLabelLength === 0 && maxNumberOfLine === 0) {
    chart.canvas.style.height = '300px'
    chart.options.layout.padding.top = 0
  } else {
    chart.canvas.style.height = `${300 + maxNumberOfLine * 14}px`
    chart.options.layout.padding.top = 32 + (maxNumberOfLine - 1) * 14
    if (currentLabelLength !== 0 && id !== null && chart.options.plugins.annotation.annotations[id]?.label) {
      chart.options.plugins.annotation.annotations[id].label.yAdjust = -32 - (currentLabelLength - 1) * 14
    }
  }
  if (update) chart.update()
}
export const generateMarkerData = (dataX, label) => {
  return {
    // Indicates the type of annotation
    type: 'line',
    xMin: dataX,
    xMax: dataX,
    xScaleID: 'x',
    borderDash: [3, 3],
    label: {
      content: label,
      display: true,
      backgroundColor: theme.palette.hpBaseDark,
      width: 50,
      position: '100%',
      padding: {
        top: 4,
        bottom: 4,
        left: 5,
        right: 5
      },
      yAdjust: -32,
      z: 50,
      borderWidth: 1
    },
    click: ({chart, element}) => {
      if (chart.options.plugins.marker.editEnabled) {
        chart?.setTooltipVisible(false)
        chart.currentEditElement = {id: element.options.id, element}
        chart.config.options.setShowMarkerTooltip(true)
        chart.markerTooltipPosition = {
          x: Number.parseInt(chart.scales.x.getPixelForTick(dataX))
        }
      }
      chart.update()
    }
  }
}
export const markerPlugin = {
  id: 'marker',
  defaults: {
    width: 1,
    errorColor: theme.palette.hpAccentRed,
    dash: [3, 3]
  },
  afterInit: (chart) => {
    chart.marker = {
      ...chart.marker,
      x: 0,
      y: 0
    }
  },
  beforeEvent: (chart, args, opts) => {
    const {scales} = chart
    const canvasPosition = getRelativePosition(args.event, chart)
    const dataX = scales.x.getValueForPixel(canvasPosition.x)
    const pixelOfTick = Number.parseInt(scales.x.getPixelForTick(dataX))
    const next = dataX + 1
    const nextPixel = Number.parseInt(scales.x.getPixelForTick(next))
    if (chart.markerTooltipPosition) return false
    if (args.event.type === 'mousemove' && Math.abs(pixelOfTick - canvasPosition.x) < 3) {
      chart.marker = {...chart.marker, drawLine: true, valueX: pixelOfTick, lineX: dataX}
    } else if (nextPixel > pixelOfTick && Math.abs(nextPixel - canvasPosition.x) < 3) {
      chart.marker = {...chart.marker, drawLine: true, valueX: nextPixel, lineX: next}
    } else {
      chart.marker = {...chart.marker, drawLine: true, valueX: pixelOfTick, lineX: dataX}
    }

    if (args.event.type === 'click' && (opts.enabled || opts.editEnabled)) {
      if (
        Object.keys(chart.config.options.plugins.annotation.annotations).includes(
          String(scales.x.getValueForPixel(chart.marker.valueX))
        )
      )
        return false
      const {draw} = chart.marker
      if (!draw) {
        chart.options.plugins.marker.enabled = false
        chart.options.plugins.marker.editEnabled = false
        chart.setIsAnyModeEnabled(false)
        changeLabelColorOnEditToggle(chart, theme.palette.hpBaseDark)
        chart.update()
        return
      }
      chart?.setTooltipVisible(false)
      if (opts.enabled) {
        chart.markerTooltipPosition = {
          x: chart.marker.valueX
        }
        chart.config.options.setShowMarkerTooltip(true)
      }
    }
  },
  afterEvent: (chart, args) => {
    const {inChartArea} = args
    const {x, y} = args.event
    chart.marker = {...chart.marker, x, y, draw: inChartArea}

    chart.draw()
  },
  beforeDatasetsDraw: (chart, args, opts) => {
    const {ctx, scales} = chart
    const {top, bottom} = chart.chartArea
    const {draw, valueX} = chart.marker || {}
    if (!draw) return
    if (!opts.enabled) return

    ctx.save()
    ctx.beginPath()
    ctx.lineWidth = opts.width
    if (
      Object.keys(chart.config.options.plugins.annotation.annotations).includes(
        String(scales.x.getValueForPixel(valueX))
      )
    ) {
      ctx.strokeStyle = opts.errorColor
      ctx.setLineDash([0, 0])
      ctx.lineWidth = 3
    } else {
      ctx.strokeStyle = opts.color
      ctx.setLineDash(opts.dash)
    }
    ctx.moveTo(valueX, bottom)
    ctx.lineTo(valueX, top)
    ctx.stroke()
    ctx.restore()
  }
}

export const markerOnEnter = ({chart, element}) => {
  chart.labelHovered = true
  let fadeOutColor
  if (chart.options.plugins.marker.editEnabled) {
    fadeOutColor = theme.palette.hpPrimaryLight
    chart.ctx.canvas.style.cursor = 'pointer'
  } else {
    fadeOutColor = theme.palette.hpBaseLight
  }
  changeColorOnMouseEvent(
    chart.config.options.plugins.annotation.annotations,
    element.options.id,
    fadeOutColor,
    theme.palette.hpBase
  )
  chart.config.options.plugins.annotation.annotations[element.options.id].label.z = 999
  chart.update()
}

export const markerOnLeave = ({chart, element}) => {
  chart.labelHovered = false
  let fadeOutColor
  if (chart.options.plugins.marker.editEnabled) {
    fadeOutColor = theme.palette.hpPrimary
    chart.ctx.canvas.style.cursor = 'default'
  } else {
    fadeOutColor = theme.palette.hpBaseDark
  }
  changeColorOnMouseEvent(
    chart.config.options.plugins.annotation.annotations,
    element.options.id,
    fadeOutColor,
    theme.palette.hpAccentBlack
  )
  chart.config.options.plugins.annotation.annotations[element.options.id].label.z = 50
  chart.update()
}

const changeColorOnMouseEvent = (annotations, id, fadeOutColor, fadeOutBorderColor) => {
  for (const key of Object.keys(annotations)) {
    if (id !== key) {
      annotations[key].label.backgroundColor = fadeOutColor
      annotations[key].label.borderColor = fadeOutBorderColor
    }
  }
}
